import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { STORAGE_KEYS } from '@/constants/storage';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { completeSsoLogin } from '@/store/authSlice';
import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import { showApiError } from '@/utils/apiError';
import {
  clearPlatformOnboardingRequired,
  isFattahOneProductSelectionRequired,
  isPlatformOnboardingRequired,
} from '@/modules/onboarding/onboardingState';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { getPostLoginPath } from '@/utils/moduleScope';
import { isOrgOwner, resolveOwnerHomePath } from '@/utils/moduleAccess';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { notify } from '@/core/toast/notify';

export function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const exchangeToken = params.get('exchangeToken');
    const redirect = params.get('redirect');
    const pendingEntryModule = sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE) ?? undefined;
    if (!exchangeToken) {
      setError('Missing SSO exchange token');
      return;
    }
    (async () => {
      try {
        const result = await dispatch(
          completeSsoLogin({
            exchangeToken,
            deviceId: getOrCreateDeviceId(),
            entryModule: pendingEntryModule,
          }),
        ).unwrap();
        notify.success('Signed in with SSO');
        if (result.masterAdmin) {
          navigate('/master', { replace: true });
          return;
        }
        if (pendingEntryModule) {
          clearPlatformOnboardingRequired();
        }
        if (isFattahOneProductSelectionRequired()) {
          navigate('/onboarding/products', { replace: true });
          return;
        }
        if (isPlatformOnboardingRequired()) {
          navigate('/onboarding', { replace: true });
          return;
        }
        if (result.entryModule || pendingEntryModule) {
          const moduleCode = (result.entryModule ?? pendingEntryModule) as string;
          const home =
            redirect?.startsWith('/')
              ? redirect
              : getModuleEntryByErpModule(moduleCode)?.homePath ?? MODULE_HUB_PATH;
          navigate(getPostLoginPath(moduleCode as never, home), { replace: true });
          return;
        }
        if (isOrgOwner(result.permissions ?? [])) {
          const home = redirect?.startsWith('/') ? redirect : resolveOwnerHomePath(result.enabledModules ?? []);
          navigate(home, { replace: true });
          return;
        }
        navigate(MODULE_HUB_PATH, { replace: true });
      } catch (err) {
        showApiError(err, 'SSO sign-in failed');
        setError('SSO sign-in failed');
      }
    })();
  }, [dispatch, navigate, params]);

  return (
    <AuthCard title="Completing sign-in" subtitle="Exchanging SSO session…">
      {error ? (
        <p className="text-center text-sm text-red-600">{error}</p>
      ) : (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      )}
    </AuthCard>
  );
}
